import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { css } from "styled-system/css";
import type { TeamInsightsResponse, TeamSummary } from "@aura/shared";
import { SHARED_THRESHOLD } from "@aura/shared";
import {
  Dumbbell,
  Figure,
  RankedBars,
  StackedBars,
  STACK_GRAYS,
  ed as chartEd,
  type StackKey,
} from "~/components/charts";
import { btn, ed, tbl } from "~/components/editorial";
import { useIsAdmin } from "~/lib/auth";
import { usePeriod } from "~/lib/period";
import {
  capitalize,
  countWord,
  fmtDate,
  fmtHours,
  fmtNum,
  fmtPct,
  periodName,
  plural,
  priorName,
} from "~/lib/format";
import { HiddenTeams, InferControl } from "./components/admin";
import { TeamReadSection, TeamsMasthead, Was } from "./components/parts";
import {
  CONCENTRATION_THRESHOLD,
  concentrationRows,
  crossTeamMatrix,
  momentumRows,
  orgDailyChange,
  paceIndex,
  perPerson,
  displayKey,
  workMixTotals,
  type MomentumRow,
} from "./model";
import { useTeamInsights } from "./queries";
import { buildTeamsDek, buildTeamsHeadline, headlineTeamIds, type TeamsStoryInput } from "./story";

export function TeamsPage() {
  const { range, window } = usePeriod();
  const isAdmin = useIsAdmin();
  const [showHidden, setShowHidden] = useState(false);
  // Only admins ever ask for hidden teams.
  const includeHidden = isAdmin && showHidden;
  const { data: raw, isLoading, error } = useTeamInsights(range, { includeHidden });
  // Hidden teams never enter the comparisons; admins see them in their own list.
  const hiddenTeams = useMemo(() => (raw?.teams ?? []).filter((t) => t.hidden), [raw]);
  const data = useMemo(
    () => (raw ? { ...raw, teams: raw.teams.filter((t) => !t.hidden) } : undefined),
    [raw],
  );

  const period = data?.period ?? { label: window.label, prevLabel: window.prevLabel };
  const now = periodName(period.label);
  const prior = priorName(period.prevLabel);
  const teams = useMemo(() => data?.teams ?? [], [data]);

  const story: TeamsStoryInput = useMemo(
    () => ({
      periodLabel: period.label,
      prevLabel: period.prevLabel,
      teams: teams.map((t) => ({
        id: t.id,
        name: t.name,
        prsMerged: t.current.prsMerged,
        prevPrsMerged: t.previous.prsMerged,
        medianHoursToFirstReview: t.current.medianHoursToFirstReview,
      })),
      orgMedianHoursToFirstReview: data?.orgMedian.medianHoursToFirstReview ?? null,
      sharedCount: data?.sharedCount ?? null,
      unassignedCount: data?.unassignedCount ?? 0,
      orgDailyChange: data ? orgDailyChange(data.norm) : null,
      holidays: data
        ? {
            current: data.norm.calendar.current.holidays.map((h) => h.name),
            previous: data.norm.calendar.previous.holidays.map((h) => h.name),
          }
        : undefined,
      momentum: data ? momentumRows(teams, data.norm) : [],
    }),
    [teams, data, period.label, period.prevLabel],
  );
  const named = headlineTeamIds(story);

  return (
    <div className={ed.page}>
      <div className={ed.inner}>
        <TeamsMasthead periodLabel={period.label} prevLabel={period.prevLabel} />
        {isAdmin && (
          <InferControl>
            <button
              type="button"
              className={btn.sentenceOutline}
              aria-pressed={showHidden}
              onClick={() => setShowHidden((v) => !v)}
            >
              {showHidden ? "Hide hidden teams" : "Show hidden teams"}
            </button>
          </InferControl>
        )}
        {includeHidden && raw && <HiddenTeams teams={hiddenTeams} />}

        {isLoading ? (
          <div className={ed.loading}>Loading teams…</div>
        ) : error || !data ? (
          <div className={ed.loading}>
            Couldn't load team stats: {String((error as Error | null)?.message ?? "no data")}
          </div>
        ) : (
          <>
            <div className={ed.kicker}>All engineering · {now}</div>
            <h1 className={ed.headline}>{buildTeamsHeadline(story)}</h1>
            <p className={ed.dek}>{buildTeamsDek(story)}</p>
            <div className={ed.byline}>
              <b>Teams</b> · <span className="num">{fmtNum(teams.length)}</span> teams worked out
              automatically, {now} against {prior}. From GitHub and JIRA.{" "}
              <span className="num">{fmtNum(data.norm.calendar.current.workingDays)}</span> working days
              against <span className="num">{fmtNum(data.norm.calendar.previous.workingDays)}</span>.
            </div>

            {teams.length === 0 ? (
              <p className={ed.prose} style={{ marginTop: 24 }}>
                No teams yet. They appear after the next inference run reads JIRA projects and repo
                activity.
              </p>
            ) : (
              <>
                <TeamReadSection range={range} />
                <TeamLedger teams={teams} norm={data.norm} prior={prior} />
                <MomentumSection rows={story.momentum ?? []} now={now} prior={prior} />
                <ComparisonSection data={data} named={named} now={now} />
                <ConcentrationSection teams={teams} now={now} prior={prior} />
                <CrossTeamSection data={data} now={now} />
                <WorkMixSection teams={teams} now={now} />
              </>
            )}

            <Footer data={data} />
          </>
        )}
      </div>
    </div>
  );
}

/* ---------------- Ledger ---------------- */

function TeamLedger({
  teams,
  norm,
  prior,
}: {
  teams: TeamSummary[];
  norm: TeamInsightsResponse["norm"];
  prior: string;
}) {
  const [searchParams] = useSearchParams();
  const suffix = searchParams.toString() ? `?${searchParams.toString()}` : "";
  const rows = [...teams].sort(
    (a, b) => b.current.prsMerged - a.current.prsMerged || a.name.localeCompare(b.name),
  );
  return (
    <>
      <div className={ed.sect}>The ledger</div>
      <h2 className={ed.h2}>Every team, this period against {prior}</h2>
      <p className={ed.prose}>
        Totals for each team, with {prior} struck through. Everyone counts once, on their primary
        team, so the columns add up to the org apart from people with no team.
      </p>
      <div className={tbl.scroll}>
        <table className={tbl.table}>
          <thead>
            <tr>
              <th>Team</th>
              <th data-num title="People on the team; small figure is how many had work this period">
                People
              </th>
              <th data-num title="Median contributor score of the people with work">Score</th>
              <th data-num title="Merged PRs per person as a share of the org's; 100% is the org">
                Pace vs. org
              </th>
              <th data-num title="PRs by team members merged in the period">PRs merged</th>
              <th data-num title="PRs merged over people with work">Per person</th>
              <th data-num title="Median hours from open to merge">Open to merge</th>
              <th data-num title="Median hours from open to the first review by someone else">
                Wait for review
              </th>
              <th data-num title="JIRA issues done in the period; moves into review or QA don't count">
                Tickets done
              </th>
              <th data-num title="Share of merged PRs that were bug fixes">Bug share</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((t) => {
              const c = t.current;
              const p = t.previous;
              return (
                <tr key={t.id}>
                  <td style={{ whiteSpace: "nowrap" }}>
                    <Link to={`/teams/${t.id}${suffix}`}>{t.name}</Link>
                    {displayKey(t.key) && t.name !== t.key && <span className={tbl.sub}>{t.key}</span>}
                  </td>
                  <td data-num>
                    <Was prior={fmtNum(p.headcount)} current={fmtNum(c.headcount)} />
                    <span className={tbl.aside}>{fmtNum(c.activeCount)} active</span>
                  </td>
                  <td data-num>
                    <Was prior={fmtNum(p.score)} current={fmtNum(c.score)} />
                  </td>
                  <td data-num>
                    <Was
                      prior={fmtIndex(paceIndex(p, norm.org.previous))}
                      current={fmtIndex(paceIndex(c, norm.org.current))}
                    />
                  </td>
                  <td data-num>
                    <Was prior={fmtNum(p.prsMerged)} current={fmtNum(c.prsMerged)} />
                  </td>
                  <td data-num>
                    <Was
                      prior={fmtNum(p.prsMergedPerPerson ?? perPerson(p.prsMerged, p.activeCount))}
                      current={fmtNum(c.prsMergedPerPerson ?? perPerson(c.prsMerged, c.activeCount))}
                    />
                  </td>
                  <td data-num>
                    <Was prior={fmtHours(p.medianHoursToMerge)} current={fmtHours(c.medianHoursToMerge)} />
                  </td>
                  <td data-num>
                    <Was
                      prior={fmtHours(p.medianHoursToFirstReview)}
                      current={fmtHours(c.medianHoursToFirstReview)}
                    />
                  </td>
                  <td data-num>
                    <Was prior={fmtNum(p.ticketsDone)} current={fmtNum(c.ticketsDone)} />
                  </td>
                  <td data-num>
                    <Was prior={fmtPct(p.bugShare)} current={fmtPct(c.bugShare)} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className={ed.method}>
        Totals per team. Score is the median contributor score of the people with work, the same
        score as the Engineers page; the org's is {fmtNum(norm.org.current.score)}. Pace vs. org is the
        team's merged PRs per person over the org's, so 100% is the org's own rate. Per person is
        merged PRs over people with work in the window. Times are team medians over merged PRs.
        Tickets are done JIRA issues only. Struck figures are from {prior}.
      </p>
    </>
  );
}

const fmtIndex = (v: number | null) => (v === null ? "–" : `${fmtNum(v)}%`);

/* ---------------- Momentum ---------------- */

function MomentumSection({ rows, now, prior }: { rows: MomentumRow[]; now: string; prior: string }) {
  const moved = rows.filter((r) => r.before !== null);
  if (moved.length < 2) return null;
  const top = moved[0];
  const bottom = moved[moved.length - 1];
  return (
    <>
      <div className={ed.sect}>Against the norm</div>
      <h2 className={ed.h2}>Who gained ground on the org, and who lost it</h2>
      <p className={ed.prose}>
        Each team's merged PRs per person as a share of the org's, {prior} to {now}. Everyone works
        the same days and holidays, so a team only moves here when it changed against everyone else.
      </p>
      <Figure
        title={
          (top?.change ?? 0) > 0 && (bottom?.change ?? 0) < 0
            ? `${top?.name} gained the most against the org; ${bottom?.name} lost the most`
            : `Pace against the org, ${prior} to ${now}`
        }
        sub="Hollow is the earlier period, filled is this one. 100% is the org's rate."
        method={`Merged PRs per person with work, divided by the same figure for the whole org (100%). Hollow is ${prior}, filled is ${now}. Teams with nobody working in ${prior} are left out.`}
        table={{
          columns: ["Team", prior, now, "Change"],
          rows: moved.map((r) => [
            r.name,
            fmtIndex(r.before),
            fmtIndex(r.now),
            r.change === null ? "–" : `${r.change > 0 ? "+" : ""}${fmtNum(r.change)} pts`,
          ]),
        }}
      >
        <Dumbbell
          rows={moved.map((r) => ({
            id: r.id,
            label: r.name,
            from: r.before,
            to: r.now,
            tone:
              r.id === top?.id && (r.change ?? 0) > 0
                ? "emph"
                : r.id === bottom?.id && (r.change ?? 0) < 0
                  ? "caution"
                  : undefined,
          }))}
          fromLabel={prior}
          toLabel={now}
          format={(v) => `${Math.round(v)}%`}
          ariaLabel={`Each team's pace against the org, ${prior} and ${now}`}
        />
      </Figure>
    </>
  );
}

/* ---------------- Comparison ---------------- */

function ComparisonSection({
  data,
  named,
  now,
}: {
  data: TeamInsightsResponse;
  named: { top: string | null; slow: string | null };
  now: string;
}) {
  const { teams, orgMedian } = data;
  const measures: Array<{
    id: string;
    title: string;
    value: (t: TeamSummary) => number | null;
    org: number | null;
    format: (v: number) => string;
    emph: string | null;
  }> = [
    {
      id: "pp",
      title: "Merged PRs per person",
      value: (t) => t.current.prsMergedPerPerson,
      org: orgMedian.prsMergedPerPerson,
      format: (v) => fmtNum(v),
      emph: named.top,
    },
    {
      id: "ttm",
      title: "Open to merge, median",
      value: (t) => t.current.medianHoursToMerge,
      org: orgMedian.medianHoursToMerge,
      format: (v) => fmtHours(v),
      emph: null,
    },
    {
      id: "wait",
      title: "Wait for first review, median",
      value: (t) => t.current.medianHoursToFirstReview,
      org: orgMedian.medianHoursToFirstReview,
      format: (v) => fmtHours(v),
      emph: named.slow,
    },
  ];
  const namedNames = teams.filter((t) => t.id === named.top || t.id === named.slow).map((t) => t.name);

  return (
    <>
      <div className={ed.sect}>I. Against the org</div>
      <h2 className={ed.h2}>How each team compares with the org median</h2>
      <p className={ed.prose}>
        Rates, not totals, so a big team and a small one can sit on the same line. Each hollow dot is
        the median across teams; the filled dot is the team.
        {namedNames.length > 0 && <> {namedNames.join(" and ")} in color, as named above.</>}
      </p>
      <Figure
        title={`Per-person throughput and review speed by team, ${now}`}
        sub="Hollow dot is the org median, filled is the team. For the two times, lower is faster."
        method="Per person: merged PRs over people with work in the window. Times: team medians over merged PRs, first review by someone other than the author, bots excluded. Org median is the median across teams."
        table={{
          columns: ["Team", "Per person", "Open to merge", "Wait for review"],
          rows: [
            ...teams.map((t) => [
              t.name,
              fmtNum(t.current.prsMergedPerPerson),
              fmtHours(t.current.medianHoursToMerge),
              fmtHours(t.current.medianHoursToFirstReview),
            ]),
            [
              "Org median",
              fmtNum(orgMedian.prsMergedPerPerson),
              fmtHours(orgMedian.medianHoursToMerge),
              fmtHours(orgMedian.medianHoursToFirstReview),
            ],
          ],
        }}
      >
        {measures.map((m) => {
          const rows = teams
            .filter((t) => m.value(t) !== null)
            .sort((a, b) => (m.value(b) ?? 0) - (m.value(a) ?? 0))
            .map((t) => ({
              id: t.id,
              label: t.name,
              from: m.org,
              to: m.value(t) ?? 0,
              tone: t.id === m.emph ? ("emph" as const) : undefined,
            }));
          if (rows.length === 0) return null;
          return (
            <div key={m.id} className={styles.measure}>
              <h4 className={styles.measureTitle}>{m.title}</h4>
              <Dumbbell
                ariaLabel={`${m.title} by team against the org median`}
                fromLabel="Org median"
                toLabel="Team"
                format={m.format}
                rows={rows}
              />
            </div>
          );
        })}
      </Figure>
    </>
  );
}

/* ---------------- Review concentration ---------------- */

function ConcentrationSection({
  teams,
  now,
  prior,
}: {
  teams: TeamSummary[];
  now: string;
  prior: string;
}) {
  const rows = concentrationRows(teams);
  if (rows.length === 0) return null;
  const over = rows.filter((r) => r.overThreshold);
  return (
    <>
      <div className={ed.sect}>II. Review bench</div>
      <h2 className={ed.h2}>
        {over.length === 0
          ? `No team leans on two reviewers for more than ${CONCENTRATION_THRESHOLD}% of its reviews`
          : `${capitalize(countWord(over.length))} of ${countWord(rows.length)} teams lean on two people for more than ${CONCENTRATION_THRESHOLD}% of reviews`}
      </h2>
      <p className={ed.prose}>
        The share of each team's reviews given by its two busiest reviewers. Above{" "}
        <span className="num">{CONCENTRATION_THRESHOLD}%</span>, one person out sick or on leave
        slows the whole team.
      </p>
      <Figure
        title={`Share of reviews by each team's top two reviewers, ${now}`}
        sub={`Teams above ${CONCENTRATION_THRESHOLD}% in red. Tick marks show ${prior}.`}
        method="Reviews given by team members in the window; the top two reviewers' share of them. Teams with no reviews are left out."
        table={{
          columns: ["Team", now, prior],
          rows: rows.map((r) => [r.name, fmtPct(r.share), fmtPct(r.prior)]),
        }}
      >
        <RankedBars
          ariaLabel="Top-two reviewer share by team"
          priorLabel={prior}
          format={(v) => `${Math.round(v)}%`}
          rows={rows.map((r) => ({
            id: r.id,
            label: r.name,
            value: r.share,
            prior: r.prior,
            caution: r.overThreshold,
          }))}
          cut={
            over.length > 0 && over.length < rows.length
              ? { after: over.length, note: `Above ${CONCENTRATION_THRESHOLD}%` }
              : undefined
          }
        />
      </Figure>
    </>
  );
}

/* ---------------- Cross-team reviews ---------------- */

function CrossTeamSection({ data, now }: { data: TeamInsightsResponse; now: string }) {
  const m = crossTeamMatrix(data.teams, data.crossTeamReviews);
  const name = (id: string) => data.teams.find((t) => t.id === id)?.name ?? id;
  const flows = data.crossTeamReviews
    .filter((e) => e.fromTeamId !== e.toTeamId && e.count > 0)
    .sort((a, b) => b.count - a.count);
  return (
    <>
      <div className={ed.sect}>III. Across teams</div>
      <h2 className={ed.h2}>
        {m.total === 0
          ? `No reviews crossed a team line in ${now}`
          : `${plural(m.total, "review")} crossed a team line in ${now}`}
      </h2>
      {m.top && (
        <p className={ed.prose}>
          The largest flow was {name(m.top.from)} reviewing {name(m.top.to)}, with{" "}
          <span className="num">{fmtNum(m.top.count)}</span>. Read across for reviews a team gave,
          down for reviews it received.
        </p>
      )}
      {m.total > 0 && (
        <Figure
          title="Who reviews whom across teams"
          sub="Rows are the reviewer's team, columns the author's."
          method="Reviews by members of one team on PRs authored by members of another, in the window. Each person counts on their primary team only."
          table={{
            columns: ["Reviewer team", "Author team", "Reviews"],
            rows: flows.map((e) => [name(e.fromTeamId), name(e.toTeamId), e.count]),
          }}
        >
          <div className={tbl.scrollPlain}>
            <table className={tbl.numeric}>
              <thead>
                <tr>
                  <th>Reviewer ↓ author →</th>
                  {m.teams.map((t) => (
                    <th key={t.id} data-num>
                      {t.name}
                    </th>
                  ))}
                  <th data-num>Given</th>
                </tr>
              </thead>
              <tbody>
                {m.teams.map((from) => (
                  <tr key={from.id}>
                    <td data-text>
                      <b>{from.name}</b>
                    </td>
                    {m.teams.map((to) => {
                      const n = m.cell(from.id, to.id);
                      return (
                        <td key={to.id} data-num data-dim={from.id === to.id || n === 0 || undefined}>
                          {from.id === to.id ? "·" : n === 0 ? "–" : fmtNum(n)}
                        </td>
                      );
                    })}
                    <td data-num>
                      <b>{fmtNum(m.given.get(from.id) ?? 0)}</b>
                    </td>
                  </tr>
                ))}
                <tr>
                  <td data-text>
                    <b>Received</b>
                  </td>
                  {m.teams.map((t) => (
                    <td key={t.id} data-num>
                      <b>{fmtNum(m.received.get(t.id) ?? 0)}</b>
                    </td>
                  ))}
                  <td data-num>
                    <b>{fmtNum(m.total)}</b>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </Figure>
      )}
    </>
  );
}

/* ---------------- Work mix ---------------- */

function WorkMixSection({ teams, now }: { teams: TeamSummary[]; now: string }) {
  const totals = workMixTotals(teams);
  const lead = totals[0];
  if (!lead) return null;
  const all = totals.reduce((s, c) => s + c.count, 0);
  const keys: StackKey[] = totals.map((c, i) => ({
    key: c.category,
    label: c.label,
    color: i === 0 ? chartEd.emph : STACK_GRAYS[Math.min(i - 1, STACK_GRAYS.length - 1)]!,
  }));
  const rows = teams
    .filter((t) => t.current.workMix.some((c) => c.count > 0))
    .sort((a, b) => b.current.prsMerged - a.current.prsMerged);
  const leadShare = (t: TeamSummary) =>
    t.current.workMix.find((c) => c.category === lead.category)?.share ?? 0;
  const most = [...rows].sort((a, b) => leadShare(b) - leadShare(a))[0];
  return (
    <>
      <div className={ed.sect}>IV. The work</div>
      <h2 className={ed.h2}>
        {lead.label} made up {fmtPct((lead.count / all) * 100)} of merged PRs across teams
      </h2>
      <p className={ed.prose}>
        Of <span className="num">{fmtNum(all)}</span> categorized merges in {now},{" "}
        <span className="num">{fmtNum(lead.count)}</span> were {lead.label.toLowerCase()}.
        {most && (
          <>
            {" "}
            {most.name} had the highest share, at{" "}
            <span className="num">{fmtPct(leadShare(most))}</span>.
          </>
        )}
      </p>
      <Figure
        title="What each team's merged PRs were"
        sub={`Share of each team's merged PRs. ${lead.label} in color, the rest in gray.`}
        method="Category from the linked JIRA issue type, else the branch and title. PRs merged in the window by team members."
        table={{
          columns: ["Team", ...keys.map((k) => k.label)],
          rows: rows.map((t) => [
            t.name,
            ...keys.map((k) => t.current.workMix.find((c) => c.category === k.key)?.count ?? 0),
          ]),
        }}
      >
        <StackedBars
          ariaLabel="Merged PRs by category per team"
          keys={keys}
          rows={rows.map((t) => ({
            id: t.id,
            label: t.name,
            values: Object.fromEntries(t.current.workMix.map((c) => [c.category, c.count])),
          }))}
        />
      </Figure>
    </>
  );
}

/* ---------------- Footer ---------------- */

function Footer({ data }: { data: TeamInsightsResponse }) {
  const shared = Math.round(SHARED_THRESHOLD * 100);
  return (
    <>
      <div className={ed.sect}>How teams are worked out</div>
      <p className={ed.prose}>
        Teams are worked out from each person's last{" "}
        <span className="num">{fmtNum(data.inferenceWindowDays)}</span> days of work, not the
        reporting period, so a quiet month doesn't move anyone.{" "}
        {data.inferredAt ? (
          <>
            Teams last worked out <span className="num">{fmtDate(data.inferredAt)}</span>.{" "}
          </>
        ) : (
          <>Teams haven't been worked out yet. </>
        )}
        <span className="num">{fmtNum(data.unassignedCount)}</span>{" "}
        {data.unassignedCount === 1 ? "person" : "people"} with work this period{" "}
        {data.unassignedCount === 1 ? "has" : "have"} no primary team.
      </p>
      <dl className={ed.defs}>
        <div>
          <dt>JIRA project first</dt>
          <dd>
            Each person goes to the team whose JIRA project holds most of their tickets, and whose
            repos hold most of their PRs and commits.
          </dd>
        </div>
        <div>
          <dt>Then repos and reviews</dt>
          <dd>
            People with no JIRA work are grouped by the repos they commit to, and failing that by
            who reviews whose code.
          </dd>
        </div>
        <div>
          <dt>Shared people</dt>
          <dd>
            When no single team holds at least {shared}% of someone's work, they are marked shared.
            They still count once, on their primary team; other teams holding 20% or more of
            their work are listed beside their name.
          </dd>
        </div>
        <div>
          <dt>Unassigned</dt>
          <dd>People with work this period who have no primary team.</dd>
        </div>
        <div>
          <dt>Which teams show</dt>
          <dd>
            Teams with at least one primary member. Hidden teams keep their people but drop out of
            these comparisons.
          </dd>
        </div>
        <div>
          <dt>Admin changes</dt>
          <dd>
            Admins can move or pin a person, name a lead, rename a team or hide it. Pinned people,
            including anyone placed on a team by hand, stay put when teams are worked out again.
            The latest pin is a person's primary team.
          </dd>
        </div>
        <div>
          <dt>Totals and rates</dt>
          <dd>
            The ledger shows team totals. The comparison uses per-person rates and team medians.
            People are never ranked across teams.
          </dd>
        </div>
      </dl>
    </>
  );
}

const styles = {
  measure: css({ mt: "14px", "&:first-child": { mt: 0 } }),
  measureTitle: css({
    fontFamily: "sans",
    fontSize: "11px",
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "ed.ink2",
    m: 0,
    mb: "2px",
  }),
};
